import { Link } from 'react-router-dom';
import {
  Mail, Phone, MapPin, Facebook, Instagram, Clock, MessageCircle, Music2,
  Home, Building2, Factory, ChevronRight, ArrowRight, ExternalLink,
} from 'lucide-react';
import logoSrc from '@/assets/lg4.webp';

const services = [
  { icon: Home, title: 'Residential Construction', desc: 'Homes & renovations.', href: '/services#residential' },
  { icon: Building2, title: 'Commercial Construction', desc: 'Offices & retail spaces.', href: '/services#commercial' },
  { icon: Factory, title: 'Industrial Construction', desc: 'Storage & facilities.', href: '/services#industrial' },
];

const quickLinks = [
  { name: 'Home', path: '/' },
  { name: 'Our Services', path: '/services' },
  { name: 'Projects', path: '/portfolio' },
  { name: 'About Us', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

const workingHours = [
  { label: 'Mon – Fri', value: '9:00 AM – 5:00 PM' },
  { label: 'Saturday', value: '9:00 AM – 1:00 PM' },
  { label: 'Sunday', value: 'Closed' },
];

const socials = [
  { icon: Facebook, href: 'https://www.facebook.com/', label: 'Facebook' },
  { icon: Instagram, href: 'https://www.instagram.com/', label: 'Instagram' },
  { icon: Music2, href: 'https://www.tiktok.com/', label: 'TikTok' },
  { icon: MessageCircle, href: 'https://wa.me/94750441260', label: 'WhatsApp' },
];

function ColumnHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <h4 className="text-white font-black uppercase tracking-wide text-[11px] mb-1.5">{children}</h4>
      <div className="w-6 h-0.5 bg-amber-400" />
    </div>
  );
}

export function Footer() {
  return (
    <footer className="relative w-full overflow-hidden" style={{ backgroundColor: '#061633' }}>
      <img
        src="/assets/foooter%20section%20back.png"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover opacity-[0.18]"
      />
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(90deg, #061633 0%, #061633 55%, rgba(6,22,51,0.88) 100%)' }}
      />

      <div className="relative z-10 max-w-[1200px] w-full mx-auto px-4 md:px-6 lg:px-8 pt-8 pb-6 sm:pb-4">
        {/* ── Main columns ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.8fr_1.2fr_0.8fr_1.2fr_1.2fr] gap-x-6 gap-y-8 lg:gap-y-0">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center gap-2.5 mb-3">
              <img src={logoSrc} alt="AST Construction" className="h-10 w-10 object-contain rounded-md bg-white/95 p-1 shrink-0" />
              <div className="leading-tight">
                <p className="font-black uppercase tracking-wide text-[13px]">
                  <span className="text-amber-400">AST</span>{' '}
                  <span className="text-white">Constructions Pvt Ltd</span>
                </p>
                <p className="text-[9px] text-slate-400 uppercase tracking-[0.18em] mt-0.5">Build Your Future</p>
              </div>
            </Link>
            <p className="text-slate-400 text-xs leading-relaxed mb-3 max-w-xs">
              Delivering high-quality construction solutions for residential, commercial, and industrial projects with precision and excellence.
            </p>
            <div className="space-y-1.5 mb-3">
              <a href="https://wa.me/94750441260" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-300 hover:text-amber-400 transition-colors text-xs">
                <Phone className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                +94 75 044 1260 (WhatsApp)
              </a>
              <a href="mailto:astconst.14@gmail.com" className="flex items-center gap-2 text-slate-300 hover:text-amber-400 transition-colors text-xs">
                <Mail className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                astconst.14@gmail.com
              </a>
              <a href="https://maps.app.goo.gl/ayxADVJQgyUUaLC37" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-300 hover:text-amber-400 transition-colors text-xs">
                <MapPin className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                33, Adiyapatham Road, Thirunelvely, Jaffna.
              </a>
            </div>
            <div className="flex items-center gap-2">
              {socials.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/15 bg-white/5 text-slate-300 transition-colors hover:border-amber-400/50 hover:bg-amber-400/15 hover:text-amber-400"
                >
                  <social.icon className="h-3.5 w-3.5" />
                </a>
              ))}
            </div>
          </div>

          {/* Our Services */}
          <div>
            <ColumnHeading>Our Services</ColumnHeading>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service.title}>
                  <Link to={service.href} className="group flex items-center gap-2.5">
                    <span className="w-7 h-7 rounded-md border border-amber-400/30 bg-amber-400/10 flex items-center justify-center shrink-0 group-hover:bg-amber-400/20 transition-colors">
                      <service.icon className="w-3.5 h-3.5 text-amber-400" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-xs font-bold text-white group-hover:text-amber-400 transition-colors leading-tight">{service.title}</span>
                      <span className="block text-[10px] text-slate-500 mt-0.5 leading-tight">{service.desc}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <ColumnHeading>Quick Links</ColumnHeading>
            <ul className="space-y-1.5">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link to={link.path} className="group flex items-center gap-1 text-xs text-slate-300 hover:text-amber-400 transition-colors">
                    <ChevronRight className="w-3 h-3 text-amber-400/70 shrink-0" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Working Hours */}
          <div>
            <ColumnHeading>Working Hours</ColumnHeading>
            <div className="rounded-lg border border-white/10 bg-white/4 px-3 py-1.5">
              {workingHours.map((row, i) => (
                <div
                  key={row.label}
                  className={`flex items-center justify-between gap-2 py-1 ${i !== workingHours.length - 1 ? 'border-b border-white/5' : ''}`}
                >
                  <span className="flex items-center gap-1.5 text-[10px] text-slate-400 uppercase tracking-wide whitespace-nowrap">
                    {i === 0 && <Clock className="w-3 h-3 text-amber-400 shrink-0" />}
                    {row.label}
                  </span>
                  <span className="text-[11px] font-semibold text-white text-right whitespace-nowrap">{row.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Find Us */}
          <div>
            <ColumnHeading>Find Us</ColumnHeading>
            <a
              href="https://maps.app.goo.gl/ayxADVJQgyUUaLC37"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative block rounded-lg overflow-hidden border border-white/10 h-28 sm:h-32 lg:h-36 w-full max-w-75"
            >
              <iframe
                src="https://maps.google.com/maps?q=9.688787599463398,80.02923415126327&z=16&output=embed"
                className="absolute inset-0 w-full h-full border-0"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="AST Construction location"
              />
              <div className="absolute inset-0 bg-transparent group-hover:bg-slate-950/10 transition-colors" />
              <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-white/95 backdrop-blur px-2 py-1 text-[10px] font-bold text-slate-800 shadow-sm">
                View on Google Maps <ExternalLink className="w-2.5 h-2.5" />
              </span>
            </a>
          </div>
        </div>

        {/* ── CTA bar ── */}
        <div className="mt-6 relative rounded-xl overflow-hidden border border-amber-400/20 bg-white/5">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-400 to-orange-500" />
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2.5 px-5 sm:px-6 py-2.5 pl-6 sm:pl-7">
            <div className="text-center sm:text-left">
              <p className="text-amber-400 text-[10px] font-black uppercase tracking-[0.18em] mb-0.5">Let's Build Together</p>
              <h3 className="text-white text-[13px] sm:text-[15px] font-black">Your Dream Project Awaits!</h3>
            </div>
            <Link to="/contact" className="shrink-0">
              <button className="inline-flex items-center gap-1.5 bg-amber-400 text-slate-900 font-black text-xs sm:text-[13px] px-4 py-2 rounded-lg hover:bg-amber-500 transition-colors whitespace-nowrap">
                Start New Project
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </Link>
          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div className="mt-4 pt-3 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-2 text-center">
          <p className="text-[11px] text-slate-500 order-1 sm:order-1">
            &copy; {new Date().getFullYear()} AST Constructions Pvt Ltd. All Rights Reserved.
          </p>
          <p className="text-[11px] text-slate-500 order-3 sm:order-2">Built for Excellence.</p>
          <p className="text-[11px] text-slate-500 order-2 sm:order-3 flex items-center gap-2">
            <span className="hover:text-amber-400 transition-colors cursor-default">Privacy Policy</span>
            <span className="text-slate-700">|</span>
            <span className="hover:text-amber-400 transition-colors cursor-default">Terms &amp; Conditions</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
